/* eslint-disable jsdoc/require-returns, jsdoc/require-param-type, jsdoc/require-param-description */
export const convertBootstrapSkuOptions = (bootstrapData) => {
  /**
   * Reduce a bootstrap SKU map to just each SKU's `options` payload.
   * Non-mutating: returns a new object, and clones each `options` value.
   * @param skus
   */
  const extractSkuOptions = (skus = {}) => Object.entries(skus ?? {}).reduce(
    (acc, [sku, product]) => ({
      ...acc,
      [sku]: {
        options: product?.options ? { ...product.options } : {
          selected: {},
          enabled: null,
          disabled: null,
        },
      },
    }),
    {},
  );

  const IMAGE_KEY_REGEX = /image/i;

  // Pull the SKU out of a buy.garmin URL, e.g.
  // https://buy-test.garmin.com/en-US/p/782585/pn/010-02645-04 → "010-02645-04"
  const extractSkuFromUrl = (url = '') => {
    const match = String(url ?? '').match(/\/pn\/([^/?#]+)/);
    return match?.[1] ?? '';
  };

  // Rebuild a res.garmin.com image URL from the punctuation-stripped slug used
  // in the bootstrap payload, e.g.
  //   httpsresgarmincomenproducts010-02645-00gcf-sm-<uuid>jpg
  // → https://res.garmin.com/en/products/010-02645-00/g/cf-sm-\<uuid\>.jpg
  const rebuildImageUrl = (slug = '') => {
    const match = String(slug ?? '').match(
      /^https(resgarmincom)(en)(products)(\d{3}-\d{5}-[A-Za-z0-9]{2})([gv])(cf-.*)(jpg|png|webp)$/i,
    );
    if (!match) return String(slug ?? '');
    const [, , , , sku, folder, rest, ext] = match;
    return `https://res.garmin.com/en/products/${sku}/${folder}/${rest}.${ext}`;
  };

  const transformSelected = (selected = {}) => Object.entries(selected ?? {}).reduce(
    (acc, [key, values]) => {
      const first = Array.isArray(values) ? values[0] : values;
      return {
        ...acc,
        [key]: IMAGE_KEY_REGEX.test(key) ? rebuildImageUrl(first) : first,
      };
    },
    {},
  );

  // Reference matrix from EMS. Used to sort the objects inside each
  // enabled/disabled array so the bootstrap-derived output matches the order
  // of the corresponding array in `window.emsData.skuVariantMatrix`.
  const referenceMatrix = (typeof window !== 'undefined'
    ? window?.emsData?.skuVariantMatrix
    : null) ?? {};

  const sortEntriesByReference = (mappedEntries, ownerSku, stateKind, key) => {
    const referenceArray = referenceMatrix?.[ownerSku]?.[stateKind]?.[key];
    if (!Array.isArray(referenceArray) || referenceArray.length === 0) {
      return mappedEntries;
    }

    const orderIndex = new Map(
      referenceArray.map((item, index) => [item?.sku ?? '', index]),
    );
    const fallback = referenceArray.length;

    return [...mappedEntries].sort((a, b) => {
      const ai = orderIndex.has(a?.sku ?? '') ? orderIndex.get(a?.sku ?? '') : fallback;
      const bi = orderIndex.has(b?.sku ?? '') ? orderIndex.get(b?.sku ?? '') : fallback;
      return ai - bi;
    });
  };

  const transformStateEntries = (entries, key, ownerSku, stateKind) => {
    const mapped = (Array.isArray(entries) ? entries : []).map((entry) => {
      const [rawKey, url] = Object.entries(entry ?? {})[0] ?? [];
      const sku = extractSkuFromUrl(url);
      const isImageKey = IMAGE_KEY_REGEX.test(key);
      const value = isImageKey ? rebuildImageUrl(rawKey) : rawKey;

      return {
        value,
        sku,
      };
    });

    return sortEntriesByReference(mapped, ownerSku, stateKind, key);
  };

  const transformState = (state = {}, ownerSku = '', stateKind = '') => Object.entries(state ?? {}).reduce(
    (acc, [key, entries]) => ({
      ...acc,
      [key]: transformStateEntries(entries, key, ownerSku, stateKind),
    }),
    {},
  );

  /**
   * Convert a bootstrap sku-options map to the new skuVariantMatrix shape.
   * - Unwraps single-item `selected` arrays.
   * - Rebuilds image URLs for `enableFilterImage`-style keys.
   * - Rewrites enabled/disabled entries from `{ key: url }` to `{ value, sku }`.
   * - Sorts output keys by SKU for stable comparison.
   * @param skus
   */
  const convertSkuOptions = (skus = {}) => Object.entries(skus ?? {})
    .sort(([a], [b]) => a.localeCompare(b))
    .reduce((acc, [sku, product]) => {
      const options = product?.options ?? {};
      return {
        ...acc,
        [sku]: {
          selected: transformSelected(options?.selected ?? {}),
          enabled: transformState(options?.enabled ?? {}, sku, 'enabled'),
          disabled: transformState(options?.disabled ?? {}, sku, 'disabled'),
        },
      };
    }, {});

  const cleanedSkuOptions = extractSkuOptions(bootstrapData);

  return convertSkuOptions(cleanedSkuOptions);
};
